import { useCurrentEtudeData } from "components/EtudeForm/hooks/useCurrentEtudeData";
import Button from "components/shared/button";
import Checkbox from "components/shared/inputs/checkbox";
import Input, { InputType } from "components/shared/inputs/input";
import { Text } from "components/shared/text";
import { useCallback, useEffect, useRef, useState } from "react";
import { useCreateEtudePDFMutation, useUpdateEtudeNoteMutation, useUpdateProjectTraceMutation } from "store/api";
import { useAppDispatch } from "store/hooks";
import { DownloadButtons } from "../SolutionDetailComponents/DownloadButtons";
import { getSchemaUrl } from "../helpers";
import { setEtudeData } from "store/etudeSlice";
import { useAuth } from "utils/hooks/useAuth";
import useTranslation from "utils/hooks/useTranslation";
import { useSalesNetwork } from "utils/hooks/useSalesNetworks";
// import Loader from "components/shared/loader";
import { message } from "antd";
import { SvgDownload } from "components/svg";
import { env } from "../../../../../env";
import {
  getLocalStorageAccessToken,
  getLocalStorageTraceId,
  setLocalStorageTraceId,
} from "utils/helpers/localStorage";
import { getMemAppToken } from "utils/helpers/project";
import { saveAs } from "file-saver";
import * as yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Loader from "components/shared/loader";
import { ProjectTrace } from "types/ProjectTrace";
import MapValuesToProjectTrace from "utils/helpers/projectTrace";

export const EtudeDownload = () => {
  const { network } = useSalesNetwork();
  const { solution, etudeId, etudeData, json, hasFXBallon } = useCurrentEtudeData();
  const updateTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showPrice, setShowPrice] = useState(!!network?.showPrice);
  const [update] = useUpdateEtudeNoteMutation();
  const dispatch = useAppDispatch();
  const { isAuth } = useAuth();
  const { language } = useTranslation();
  const [isPdfURLLoading, setIsPdfURLLoading] = useState<boolean>(false);
  const [createEtudePDF] = useCreateEtudePDFMutation();
  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [blobImage, setBlobImage] = useState<string>("");
  const [isNoteUpdated, setIsNoteUpdated] = useState<boolean>(false);
  const defaultBrand =
    network && network.brands && network.brands.length > 0
      ? network.brands[0].brand
      : null;

  const [updateProjectTrace] = useUpdateProjectTraceMutation();
  const schema = yup.object({
    notes: yup.string().max(500).notLongWord(),
  });
  const { t } = useTranslation();
  const { control, trigger } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: {
      notes: json?.notes || "",
    },
  });

  useEffect(() => {
    onDownload();
  }, [])

  useEffect(() => {
    const url = solution?.code_schema?.pdf && getSchemaUrl(solution.code_schema.pdf);
    if (!url) { setBlobImage(""); return; }
    let token = getLocalStorageAccessToken() || getMemAppToken();
    if (!token) { setBlobImage(""); return; }
    fetch(url, { method: "GET", headers: { Authorization: "Bearer " + token } })
      .then((res) => res.ok ? res.blob() : Promise.reject(res.statusText))
      .then((blob) => setBlobImage(URL.createObjectURL(blob)))
      .catch(() => setBlobImage(""));
  }, [solution?.code_schema?.pdf]);

  const onDownload = async (_showPrice: any = showPrice) => {
    try {
      setIsPdfURLLoading(true);
      const pdfUrl = await createEtudePDF({
        etudeIds: JSON.stringify([etudeData?.etudeId]),
        showPrice: _showPrice,
        isEtudeDetail: true,
        culture: language!,
        networkId: network?.id,
      }).unwrap();
      setIsPdfURLLoading(false);

      setPdfUrl(pdfUrl);
      // const newTab = window.open(new URL(pdfUrl).toString(), "_blank");
      // if (newTab) {
      //   newTab.focus();
      // }
    } catch (error) {
      setIsPdfURLLoading(false);
      message.error("Error opening PDF: Something went wrong");
      console.error("Error opening PDF:", error);
    }
  };

  const updateTrace = useCallback(async () => {
    let id = getLocalStorageTraceId();
    let projectTrace: ProjectTrace = MapValuesToProjectTrace(
      {
        Id: id || 0,
        network: network?.code,
        installationType: etudeData?.installationType,
        json,
        systeme: json?.systeme,
        ...solution,
        etudeData,
        downloaded: true,
      },
      t
    );
    let project = await updateProjectTrace(projectTrace);

    if (project?.data?.id) {
      setLocalStorageTraceId(project.data.id);
    }
  }, [])

  const onOpen = useCallback(() => {
    const newWindow = window.open(new URL(pdfUrl).toString(), "_blank");
    if (newWindow) {
      updateTrace();
      newWindow.location.href = new URL(pdfUrl).toString();
      newWindow.focus();

    } else {
      message.error("Failed to open a new tab.");
    }
  }, [pdfUrl])

  const onDownloadCSVClick = async () => {
    const token = getLocalStorageAccessToken() || getMemAppToken();

    if (!token) {
      message.error("Error opening CSV: Something went wrong");
      return;
    }

    try {
      const response = await fetch(
        `${env.api_url}/api/Etude/DownloadEtudeCSV?etudeVersionId=${etudeData?.etudeId}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch CSV");
      }

      const blob = await response.blob();
      saveAs(blob, `${etudeData?.siteName ?? "etude"}.csv`);
    } catch (error) {
      console.error(error);
      message.error("Error opening CSV: Something went wrong");
    }
  };

  const updateNotes = useCallback(
    async (value: string) => {
      const isValid = await trigger("notes");
      if (!isValid) return;

      if (updateTimeout.current) {
        setIsNoteUpdated(false);
        clearTimeout(updateTimeout.current);
      }
      updateTimeout.current = setTimeout(() => {
        !isNaN(Number(etudeId)) &&
          isAuth &&
          update({ etudeVersionId: Number(etudeId), note: value });

        dispatch(
          setEtudeData({
            ...etudeData!,
            json: JSON.stringify({ ...json, notes: value }),
          })
        );

        setIsNoteUpdated(true);
      }, 300);
    },
    [etudeData, json, trigger]
  );

  const onChangeTarif = useCallback((e: any) => {

    setShowPrice(e.target.checked)
    onDownload(e.target.checked);
  }, []);
  // const server_url = env.app_token_url || "";
  return (
    <group data-length="370" data-direction="column" data-gap="24">
      {isPdfURLLoading && <Loader />}
      <Text
        data-weight="600"
        data-ellipsis=""
        data-color="grey-color-dark"

      //data-wrap="wrap"
      >
        FO_DOC_UPLOAD
      </Text>
      {network?.showPrice && (
        <Checkbox
          simple
          checked={showPrice}
          label="FO_DOC_PRICE"
          onChange={(e) => {
            onChangeTarif(e);
          }}
        ></Checkbox>
      )}
      <group data-gap="15">
        {/* <form
          action={server_url + "/api/showEtude"}
          method="post"
          target={"_blank"}
        >
          <input
            type="hidden"
            name="user_token"
            value={getLocalStorageAccessToken()?.toString()}
          />
          <input
            type="hidden"
            name="etudeIds"
            value={JSON.stringify([etudeData?.etudeId])}
          />
          <input type="hidden" name="showPrice" value={showPrice.toString()} />
          <input type="hidden" name="isEtudeDetail" value="true" />
          <input type="hidden" name="networkId" value={network?.id} />
          <input type="hidden" name="culture" value={language!} />
        </form>         */}
        <Button
          type="submit"
          accent
          data-height="40"
          text="FO_DOC_SUMMARY"
          onClick={onOpen}
        >
          {/* {isPdfURLLoading && <Loader />} */}
          <icon data-svg-size="16">
            <SvgDownload />
          </icon>
        </Button>
        {!hasFXBallon(solution) && <>
          <Button
            accent
            data-height="40"
            text="FO_BUTTON_CSV"
            onClick={onDownloadCSVClick}
          >
            <icon data-svg-size="16">
              <SvgDownload />
            </icon>
          </Button>
          <DownloadButtons solution={solution!} disabled={!blobImage} />
        </>}
      </group>
      <Controller
        name="notes"
        control={control}
        render={({ field, fieldState }) => (
          <>
            <Input
              type={InputType.TextArea}
              label="FO_DOC_DEFINITION"
              name={field.name}
              resize=""
              data-resize="y"
              data-height="210"
              onChange={(e) => {
                field.onChange(e);
                updateNotes(e.target.value);
              }}
              style={{ width: "90%" }}
              value={field.value}
              button={
                <group>
                  {(
                    <Button
                      micro
                      onClick={() => onDownload()}
                      disabled={!isNoteUpdated || !!fieldState.error?.message || !fieldState.isDirty}
                      {...(defaultBrand && defaultBrand === "ATLANTIC"
                        ? { "data-background": "success-color-light" }
                        : { "data-background": "main" })}
                    >
                      <icon data-svg-size="16">
                        done
                      </icon>
                    </Button>
                  )}
                </group>
              }
            />
            {fieldState.error && (
              <p style={{ color: "red", fontSize: "12px" }}>
                {fieldState.error.message}
              </p>
            )}
          </>
        )}
      />
    </group>
  );
};
